import { getMyCompaniesWithRetries } from "@/services/subscriptionService";
import {
  isAuthRoutePath,
  isProtectedPath,
  isPublicBlogPath,
  isPublicMarketingRoute,
} from "@/utils/protectedRoutes";

export interface ProtectedLayoutRedirectContext {
  currentPath: string;
  user: boolean;
  authResolved: boolean;
  hasRedirected: boolean;
  mfaChallenge: boolean;
  justLoggedIn: boolean;
}

export function devProtectedLayoutLog(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.warn(...args);
  }
}

export function isPublicRouteForLayout(pathname: string): boolean {
  return isPublicMarketingRoute(pathname) || isPublicBlogPath(pathname);
}

/**
 * Clear Redirect Flags On Protected Route.
 */
export function clearRedirectFlagsOnProtectedRoute(currentPath: string): void {
  if (!isProtectedPath(currentPath)) {
    return;
  }

  const justLoggedIn = sessionStorage.getItem("just_logged_in") === "true";
  const isRedirectingToDashboard = sessionStorage.getItem("redirecting_to_dashboard") === "true";

  if (!justLoggedIn && !isRedirectingToDashboard) {
    return;
  }

  devProtectedLayoutLog(
    "🔧 ProtectedLayout: Clearing redirect flags on protected route (early check)",
    {
      path: currentPath,
      justLoggedIn,
      isRedirectingToDashboard,
    }
  );
  sessionStorage.removeItem("just_logged_in");
  sessionStorage.removeItem("redirecting_to_dashboard");
}

export function shouldResetRedirectOnPublicRoute(
  isOnPublicRoute: boolean,
  user: boolean,
  authResolved: boolean
): boolean {
  return isOnPublicRoute && !user && authResolved;
}

/**
 * Should Redirect Just Logged In On Public Route.
 */
export function shouldRedirectJustLoggedInOnPublicRoute(
  context: ProtectedLayoutRedirectContext,
  isOnPublicRoute: boolean
): boolean {
  const isOnBlogRoute = context.currentPath === "/blog" || context.currentPath.startsWith("/blog/");

  return (
    context.authResolved &&
    context.user &&
    !context.hasRedirected &&
    !isAuthRoutePath(context.currentPath) &&
    !isOnBlogRoute &&
    isOnPublicRoute &&
    context.justLoggedIn
  );
}

export async function resolvePostLoginDestination(
  accessToken: string | undefined
): Promise<string> {
  try {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const companies = await getMyCompaniesWithRetries(accessToken);
    return companies.length > 0 ? `/c/${companies[0].slug}` : "/dashboard";
  } catch {
    return "/dashboard";
  }
}

export function shouldShowFamilyDevPreview(pathname: string): boolean {
  return import.meta.env.DEV && pathname === "/family";
}

export type ProtectedLayoutRenderPhase =
  | "loading"
  | "public-null"
  | "mfa-wait"
  | "dashboard-hr-wait"
  | "auth-grace"
  | "family-dev-preview"
  | "redirect-sign-in"
  | "authenticated";

export interface ProtectedLayoutRenderInput {
  loading: boolean;
  user: boolean;
  mfaChallenge: boolean;
  authResolved: boolean;
  currentPath: string;
  search: string;
  dashboardHrCheckComplete: boolean;
  isFamilyHubDevPreviewActive: (search: string) => boolean;
  shouldDeferUnauthenticatedRedirect: (hasUser: boolean, authResolved: boolean) => boolean;
}

/**
 * Resolve Protected Layout Render Phase.
 */
export function resolveProtectedLayoutRenderPhase(
  input: ProtectedLayoutRenderInput
): ProtectedLayoutRenderPhase {
  const {
    loading,
    user,
    mfaChallenge,
    authResolved,
    currentPath,
    search,
    dashboardHrCheckComplete,
    isFamilyHubDevPreviewActive,
    shouldDeferUnauthenticatedRedirect,
  } = input;

  if (loading && !user) {
    return "loading";
  }

  if (!user && isPublicRouteForLayout(currentPath)) {
    return "public-null";
  }

  if (mfaChallenge) {
    return "mfa-wait";
  }

  if (currentPath === "/dashboard" && !dashboardHrCheckComplete && user && authResolved) {
    return "dashboard-hr-wait";
  }

  if (
    !user &&
    (loading || shouldDeferUnauthenticatedRedirect(false, authResolved) || !authResolved)
  ) {
    return "auth-grace";
  }

  if (shouldShowFamilyDevPreview(currentPath) && isFamilyHubDevPreviewActive(search)) {
    return "family-dev-preview";
  }

  if (!user) {
    return "redirect-sign-in";
  }

  return "authenticated";
}
